import { createSelector } from "@ngrx/store";
import { CustomerManagementSelector, TwoFactorAuthMethod } from "@obg/ngx.core";

import { CommonState } from "../state";
import { TwoFactorAuthState } from "../two-factor-auth/two-factor-auth.model";

import {
	authMethodIconDetailsDictionary,
	CommunicationChannel,
	TwoFactorAuthCodeCommonVerificationErrors,
	TwoFactorChallengeType,
} from "./two-factor-auth.const";

export namespace TwoFactorAuthSelector {
	const crsKeyPrefix = "two-factor-auth";

	export const getState = (state: CommonState): TwoFactorAuthState => state.twoFactorAuth;

	export const getSelectedMethod = createSelector(getState, (state: TwoFactorAuthState) => state.selectedMethod);

	export const getSelectedChallengeType = createSelector(
		getState,
		(state: TwoFactorAuthState) => state?.challengeDetails?.challengeType
	);
	export const getLoginToken = createSelector(getState, (state: TwoFactorAuthState) => state.challengeDetails?.token);

	export const getOneTimePassword = createSelector(
		getState,
		({ oneTimePassword }: TwoFactorAuthState) => oneTimePassword || ""
	);

	export const getCodeIsValid = createSelector(
		getState,
		({ oneTimePassword }: TwoFactorAuthState) => oneTimePassword?.length === 6
	);

	export const getErrorCode = createSelector(getState, ({ errorCode }: TwoFactorAuthState) => errorCode);

	export const getPasswordErrorCode = createSelector(
		getState,
		({ passwordErrorCode }: TwoFactorAuthState) => passwordErrorCode
	);

	export const hasError = createSelector(
		getCodeIsValid,
		getErrorCode,
		(isValid, errorCode) => !!errorCode || !isValid
	);

	export const getIsBusy = createSelector(getState, ({ isBusy }): boolean => isBusy);

	export const getIsLoginPasswordVerified = createSelector(
		getState,
		({ isLoginPasswordVerified }): boolean => isLoginPasswordVerified
	);
	export const getVerificationDialogHeaderTitle = createSelector(getSelectedMethod, (selectedMethod) =>
		selectedMethod === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
			? `${crsKeyPrefix}.email-header-title`
			: `${crsKeyPrefix}.sms-header-title`
	);

	export const getChangeMethodText = createSelector(getSelectedMethod, (selectedMethod) =>
		selectedMethod === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
			? `${crsKeyPrefix}.change-method.to-sms`
			: `${crsKeyPrefix}.change-method.to-email`
	);

	export const getIconDetails = createSelector(
		getSelectedMethod,
		(selectedMethod) => authMethodIconDetailsDictionary[selectedMethod!]
	);

	export const getButtonText = createSelector(getSelectedMethod, (selectedMethod) =>
		selectedMethod === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
			? `${crsKeyPrefix}.dialog.verify-btn-email`
			: `${crsKeyPrefix}.dialog.verify-btn-sms`
	);

	export const getMaskedPhoneNumberWithExtension = createSelector(
		getState,
		(state: TwoFactorAuthState) =>
			state.challengeDetails?.communicationMethods?.find((method) => method.channel === CommunicationChannel.SMS)
				?.details
	);

	export const getMaskedEmailAddress = createSelector(
		getState,
		(state: TwoFactorAuthState) =>
			state.challengeDetails?.communicationMethods?.find(
				(method) => method.channel === CommunicationChannel.Email
			)?.details
	);

	export const getCustomerContactInfo = createSelector(
		getSelectedMethod,
		CustomerManagementSelector.getCustomerPhoneNumberWithExtension,
		CustomerManagementSelector.getCustomerEmailAddress,
		getMaskedPhoneNumberWithExtension,
		getMaskedEmailAddress,
		(
			selectedMethod,
			customerPhoneNumberWithExtension,
			customerEmailAddress,
			maskedPhoneNumberWithExtension,
			maskedEmailAddress
		) =>
			selectedMethod === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
				? customerEmailAddress ?? maskedEmailAddress
				: customerPhoneNumberWithExtension ?? maskedPhoneNumberWithExtension
	);

	export const getOtpToken = createSelector(getState, ({ token }: TwoFactorAuthState) => token);

	export const getEnabledMethods = createSelector(
		getState,
		({ enabledMethods }: TwoFactorAuthState) => enabledMethods
	);

	export const getAreBothMethodsEnabled = createSelector(
		getState,
		({ challengeDetails, enabledMethods }) =>
			challengeDetails?.communicationMethods?.length === 2 || enabledMethods?.length === 2
	);

	export const getSuccessOperationType = createSelector(
		getState,
		({ successOperationType }: TwoFactorAuthState) => successOperationType
	);

	export const getVerificationToken = createSelector(getState, ({ loginToken }) => loginToken);

	export const getPassword = createSelector(getState, ({ password }) => password);

	export const selectChallengeDetails = createSelector(
		getState,
		(state: TwoFactorAuthState) => state?.challengeDetails
	);

	export const getIsOtpExpired = createSelector(
		getState,
		({ errorCode }) => errorCode === TwoFactorAuthCodeCommonVerificationErrors.ExpiredCode
	);

	export const getCurrentDevice = createSelector(getState, (state) => state?.currentDevice);

	export const getRemainingTokenRequests = createSelector(
		getState,
		({ remainingAttempts }) => remainingAttempts || 0
	);

	export const isSuspiciousLogin = createSelector(
		getState,
		({ challengeDetails }) => challengeDetails?.challengeType === TwoFactorChallengeType.SuspiciousLogin
	);
}
